import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from '../../../../../utils/helpers';
import './Lineup.css'; // Assuming the CSS is generic enough

// Configuration for sport-specific display elements
const sportConfigs = {
  Poker: {
    header: 'CHIPS',
    field: 'chipCount',
    format: (value) => (typeof value === 'number' ? formatNumber(value) : 'N/A'),
    tiebreaker: "Captain's cashes count as tiebreaker.",
  },
  Golf: {
    header: 'ODDS',
    field: 'odds',
    format: (value) => (typeof value === 'number' ? `+${value}` : 'N/A'),
    tiebreaker: "Captain's scores count as tiebreaker.",
  },
  Chess: {
    header: 'RATING',
    field: 'rating',
    format: (value) => (typeof value === 'number' ? value : 'N/A'),
    tiebreaker: "Captain's points count as tiebreaker.",
  },
  Esports: {
    header: 'SEED',
    field: 'seed',
    format: (value) => (typeof value === 'number' ? value : 'N/A'),
    tiebreaker: "Captain's score counts as tiebreaker.",
  },
  // Add other sports or a default if needed
  Default: {
    header: 'DATA',
    field: null,
    format: () => '-',
    tiebreaker: 'Captain value is tiebreaker.',
  }
};

const LineupSalary = ({ lineup, salaryCap, onPlayerRemove, totalPlayers, format, sport }) => {
  // Get the configuration for the current sport
  const config = sportConfigs[sport] || sportConfigs.Default;

  const remainingSalary = useMemo(() => {
    const usedSalary = lineup.reduce((total, slot) => {
      return slot.player ? total + slot.player.salary : total;
    }, 0);
    return salaryCap - usedSalary;
  }, [lineup, salaryCap]);

  const availableSlots = totalPlayers - lineup.filter(slot => slot.player).length;

  const avgRemainingPerPlayer = useMemo(() => {
    return availableSlots > 0 ? remainingSalary / availableSlots : 0;
  }, [remainingSalary, availableSlots]);

  return (
    <div className="lineup-container">
      <div className="lineup-info">
        <h4 className="lineup-word-text"><span>LINEUP</span></h4>
        <div className="lineup-remaining">
          <span className="lineup-avg-remaining-text">Avg. Rem./Player:
            <span className="lineup-avg-remaining-number">${formatNumber(avgRemainingPerPlayer)}</span>
          </span>
            <span className={`lineup-remaining-salary-text`}>
              Rem. Salary:
              <span className={`lineup-remaining-salary-number ${remainingSalary < 0 ? 'over-salary-cap' : ''}`}>
                ${formatNumber(remainingSalary)}
              </span>
            </span>

        </div>
      </div>

      <table className="lineup-table">
        <thead>
          <tr>
            <th>POS.</th>
            <th>NAME</th>
            {/* Use dynamic header */}
            <th>{config.header}</th>
            <th>SALARY</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {lineup.map((slot, index) => (
            <tr key={index}>
              <td data-position={slot.position}>{slot.position === 'Cpt' ? 'Cpt*' : 'Flex'}</td>
              <td>{slot.player ? slot.player.name : 'Empty'}</td>
              {/* Use dynamic field access and formatting */}
              <td>{slot.player && config.field ? config.format(slot.player.sportSpecificData?.[config.field]) : '-'}</td>
              <td>{slot.player ? `$${formatNumber(slot.player.salary)}` : '-'}</td>
              <td className="button-cell">
                {slot.player && (
                  <button onClick={() => onPlayerRemove(index)} aria-label={`Remove ${slot.player.name}`}>
                    ×
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Use dynamic tiebreaker info */}
      <div className="captain-bonus-info">
        * {config.tiebreaker}
      </div>

    </div>
  );
};

LineupSalary.propTypes = {
  lineup: PropTypes.arrayOf(PropTypes.shape({
    position: PropTypes.string,
    player: PropTypes.shape({
      name: PropTypes.string.isRequired,
      salary: PropTypes.number.isRequired,
      // Make sportSpecificData more generic
      sportSpecificData: PropTypes.object 
    })
  })).isRequired,
  salaryCap: PropTypes.number.isRequired,
  onPlayerRemove: PropTypes.func.isRequired,
  totalPlayers: PropTypes.number.isRequired,
  format: PropTypes.string.isRequired, // Keep format if used elsewhere, though sport seems more direct here
  sport: PropTypes.string.isRequired // Add sport prop
};

export default LineupSalary; 